/**
 * Handoff de sesiune de chat între SUBDOMENIILE DECLARATE ale aceluiași bot.
 *
 * `sessionStorage` e per-origine, deci la o navigare cross-subdomeniu (ex.
 * spedisci.online → track.spedisci.online, ambele în `allowed_origins`)
 * conversația s-ar pierde: alt session id → serverul nu mai găsește firul,
 * modelul pierde contextul, iar bulele afișate + „pending"-ul dispar.
 *
 * Cărăm session id-ul + starea de chat prin FRAGMENTUL URL-ului țintă
 * (`#aig_s=...`), DOAR spre origini din `allowedOrigins` - same-site (același
 * domeniu înregistrabil), nu cross-site tracking. Fără cookie și fără storage
 * persistent: fragmentul se șterge imediat pe originea nouă, nu ajunge nici pe
 * serverul clientului, nici în istoricul vizibil.
 */

import type { ChatState } from "./chatHistory";
import type { WidgetUiState } from "./widgetState";

export interface ChatCarry {
  /** session id-ul care leagă firul (conversations.session_id) - vezi chatSession.ts */
  sid: string;
  /** starea completă a conversației (mesaje + pending + trail) - vezi chatHistory.ts */
  chat: ChatState;
  /** starea UI a widgetului (deschis/poziție), ca să rămână la fel peste hop */
  ui?: WidgetUiState;
}

const FRAGMENT_KEY = "aig_s";

/** base64 UTF-8-safe (mesajele pot avea diacritice/emoji). */
export function encodeChatCarry(carry: ChatCarry): string {
  return btoa(String.fromCharCode(...new TextEncoder().encode(JSON.stringify(carry))));
}

/** Invers, fail-closed la orice bază64 invalid sau formă neașteptată. */
export function decodeChatCarry(raw: string): ChatCarry | null {
  try {
    const json = new TextDecoder().decode(Uint8Array.from(atob(raw), (c) => c.charCodeAt(0)));
    const c = JSON.parse(json) as ChatCarry;
    if (typeof c?.sid !== "string" || !c.chat || !Array.isArray(c.chat.messages)) return null;
    return c;
  } catch {
    return null;
  }
}

/** Lipește carry-ul pe URL-ul țintă, păstrând o eventuală rută SPA (`#/...`). */
export function withChatCarry(target: URL, carry: string): string {
  const sep = target.hash ? "&" : "#";
  return target.href + sep + FRAGMENT_KEY + "=" + encodeURIComponent(carry);
}

/**
 * Ia carry-ul din fragmentul curent și-l ȘTERGE din URL (o singură trecere,
 * păstrând restul fragmentului - ex. o rută SPA). `null` dacă lipsește sau e
 * corupt.
 */
export function takeChatCarryFromUrl(): ChatCarry | null {
  const hash = location.hash;
  if (!hash.includes(`${FRAGMENT_KEY}=`)) return null;
  // Parsare pe segmente (ca la builder): scoatem DOAR `aig_s`, păstrăm restul
  // fragmentului neatins (ex. o rută SPA `#/comenzi`).
  let raw: string | null = null;
  const rest: string[] = [];
  for (const seg of hash.replace(/^#/, "").split("&")) {
    if (!seg) continue;
    if (seg.startsWith(`${FRAGMENT_KEY}=`)) raw = seg.slice(FRAGMENT_KEY.length + 1);
    else rest.push(seg);
  }
  if (raw === null) return null;
  const cleaned = rest.length > 0 ? `#${rest.join("&")}` : "";
  history.replaceState(null, "", location.pathname + location.search + cleaned);
  return decodeChatCarry(decodeURIComponent(raw));
}
